<template>
  <div class="q-mt-md">
    <!-- Header Of the page -->
    <h3 class="text-bold q-m-md inline-block">
      {{ headerMessage }}
    </h3>

    <!-- Upload image to direct url link -->
    <div class="q-mb-lg">
      <q-uploader
        :color="getEnv === 'stage' ? 'secondary' : 'primary'"
        :text-color="getEnv === 'stage' ? 'black' : 'white'"
        label="Upload Image"
        :url="apiURL.concat('/image/upload')"
        :headers="[{ name: 'Authorization', value: `Bearer ${bearerToken}` }]"
        style="max-width: 1140px"
        multiple
        @uploaded="(val) => uploadImage(val, 'success')"
        @failed="(val) => uploadImage(val, 'fail')"
        @added="(event) => fileAdded(event)"
      />
    </div>

    <q-card v-if="getImageLoading" class="loading-wrapper">
      <div>
        <q-spinner color="primary" size="3em" />
      </div>
    </q-card>

    <q-card class="q-mb-lg" v-else>
      <!-- All images on a Select with search -->
      <q-card-section>
        <div class="row items-center justify-center">
          <div class="col-10">
            <q-select
              v-model="selectedImage"
              :options="imagesList"
              class="q-ml-sm q-px-lg"
              outlined
              use-input
              input-debounce="0"
              @update:model-value="(val) => onSelectChange(val)"
              @filter="filterFn"
            />
          </div>
          <div class="col-2">
            <q-btn
              @click="() => refreshImages()"
              :color="getEnv === 'stage' ? 'secondary' : 'primary'"
              :text-color="getEnv === 'stage' ? 'black' : 'white'"
            >
              <font-awesome-icon icon="fa-arrows-rotate" size="lg"
            /></q-btn>
            <q-btn class="q-ml-md" @click="deleteEventModal = true" color="red">
              <font-awesome-icon icon="fa-trash"
            /></q-btn>
          </div>
        </div>
      </q-card-section>

      <!-- Image preview -->
      <q-card-section class="flex justify-center">
        <q-img
          :src="baseImageURL.concat(selectedImage)"
          spinner-color="primary"
          class="fit-image"
        />
      </q-card-section>

      <!-- Display image url -->
      <q-card-section>
        <span class="flex justify-center items-center">
          <q-btn
            flat
            @click="() => clickToCopy(baseImageURL.concat(selectedImage))"
          >
            <font-awesome-icon icon="fa-copy" size="xl" color="gray" />
          </q-btn>
          {{ baseImageURL.concat(selectedImage) }}
        </span>
      </q-card-section>
    </q-card>
    <q-dialog v-model="deleteEventModal">
      <q-card class="card-modal">
        <q-card-section>
          <h6 class="q-ml-sm">Delete Image?</h6>
        </q-card-section>

        <q-card-actions class="flex justify-between q-ma-md">
          <q-btn flat label="Cancel" color="primary" v-close-popup />
          <q-btn label="Delete" color="primary" @click="deleteImage()" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { copyToClipboard } from "quasar";

import { adminApi } from "@/utils";
import { AppConfig } from "@/app.config";

export default {
  name: "ImagesView",
  inject: ["$msal"],
  data() {
    return {
      bearerToken: null,
      deleteEventModal: false,
      adminApi,
      headerMessage: "Images",
      baseImageURL: "",
      selectedImage: "",
      apiURL: "",
      imagesList: [],
      allImagesList: [],
    };
  },

  // Async mount to wait for the actionGetImages on the api
  async mounted() {
    await this.actionGetImages().then((response) => {
      this.imagesList = response;
      this.allImagesList = response;
      if (response) this.selectedImage = response[0];
    });
    this.baseImageURL = AppConfig.aarpBlob + (await this.getEnvFolder());
    this.apiURL = await this.adminApi();
    this.bearerToken = await this.$msal.acquireToken();
  },

  computed: {
    ...mapGetters({
      getEnv: "selectEnv/getEnv",
    }),
    ...mapGetters({
      getUser: "auth/getUser",
    }),
    ...mapGetters({
      getImageLoading: "selectImage/getImageLoading",
    }),
  },

  methods: {
    ...mapActions({
      actionGetImages: "selectImage/actionGetImages",
      actionDeleteImage: "selectImage/actionDeleteImage",
    }),

    async fileAdded(file) {
      if (file) {
        this.bearerToken = await this.$msal.acquireToken();
      }
    },

    async getEnvFolder() {
      if (this.getEnv === "stage") {
        return "arenax-aarp/stage/images/";
      }
      return "arenax-aarp/production/images/";
    },

    onSelectChange(val) {
      if (val) {
        this.selectedImage = val;
        return;
      }
      this.selectedImage = this.allImagesList[0];
    },

    clickToCopy(val) {
      copyToClipboard(val)
        .then(() => {
          this.$q.notify({
            type: "positive",
            message: `Text Copied`,
          });
        })
        .catch(() => {
          // fail
        });
    },

    // Call back to api to refresh all images
    async refreshImages() {
      await this.actionGetImages().then((response) => {
        this.imagesList = response;
        this.allImagesList = response;
      });
      this.selectedImage = this.imagesList[0];
    },

    async uploadImage(val, stats) {
      if (stats === "success") {
        this.$q.notify({
          type: "positive",
          message: `Image Added`,
        });
        await this.refreshImages();
      } else {
        this.$q.notify({
          type: "negative",
          message: `Image Failed`,
        });
      }
    },

    async deleteImage() {
      this.deleteEventModal = false;
      await this.actionDeleteImage(this.selectedImage)
        .then(() => {
          this.$q.notify({
            type: "positive",
            message: `Image Deleted`,
          });
          this.refreshImages();
        })
        .catch((err) => {
          this.deleteEventModal = false;
          this.$q.notify({
            type: "negative",
            message: `Error to Delete image`,
          });
          console.log(err);
        });
    },

    // Filter search images by names
    filterFn(val, update) {
      if (val === "") {
        update(() => {
          this.imagesList = this.allImagesList;
        });
        return;
      }

      update(() => {
        const needle = val.toString().toLowerCase();
        this.imagesList = this.allImagesList.filter(
          (img) => img.toString().toLowerCase().indexOf(needle) > -1
        );
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.fit-image {
  max-width: 756px;
  height: auto;
  object-fit: fill;
  border: solid 3px $primary;
}

.loading-wrapper {
  min-height: 600px;
  position: relative;
  div {
    position: absolute;
    left: 50%;
    top: 10%;
  }
}

.card-modal {
  width: 600px;
  max-width: 80vw;
}
</style>
